/* Shared catalogue-CAD upgrade for the remaining application calculators.
   The engineering model owns force and motion; CAD is placed only when the
   selected product's published pin range covers the complete calculated path. */
(function () {
  'use strict';
  const root = document.getElementById('fg-application-calculator');
  const host = window.FGApplicationHost;
  const catalogue = window.FGActuatorSVGCatalogue;
  const engine = window.FGActuatorSVG;
  const config = window.FGApplicationCADConfig;
  if (!root || !host || !catalogue || !engine || !config || host.profile.id === 'marine-hatch') return;
  const $ = selector => root.querySelector(selector);
  const esc = value => String(value ?? '').replace(/[&<>"']/g, char => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
  const fmt = value => Number(value).toLocaleString(undefined, {maximumFractionDigits:2});
  const ratedForce = variant => {
    const match = String(variant.title || '').match(/([\d,.]+)\s*(?:lb|lbs|lbf)\b/i);
    return match ? Number(match[1].replace(/,/g, '')) : 0;
  };
  const isTVTrolley = host.profile.id === 'mobile-display';
  const columnForce = {'electric-column-lift-650p24mm': 180, 'electric-column-lift-990p6mm': 80};
  const familyName = entry => isTVTrolley && entry.handle === 'electric-column-lift' ? 'Electric Column Lift Actuator' : entry.family;
  const entries = catalogue.entries.filter(entry => (entry.motionType === 'rod' || (isTVTrolley && entry.motionType === 'column')) && Number.isFinite(entry.closedMm) && Number.isFinite(entry.openMm) && entry.openMm > entry.closedMm);
  const families = catalogue.families.filter(family => entries.some(entry => entry.handle === family.handle));
  const choices = entries.flatMap(entry => (entry.variants || [])
    .filter(variant => variant.available !== false && !(entry.pendingVariantIds || []).includes(variant.id))
    .map(variant => ({entry, variant, force: entry.motionType === 'column' ? columnForce[entry.id] || 0 : ratedForce(variant)})));
  // The legacy engineering-match/PDF list contains rod actuators only. Add
  // the published column models in this page's runtime, then recalculate once
  // so every tab and the PDF use the same qualified product as the CAD picker.
  const studioEntries = window.FGStairCatalogue?.entries;
  const studioColumnsAdded = !!(isTVTrolley && studioEntries && choices.filter(choice => choice.entry.motionType === 'column').map(choice => {
    if (studioEntries.some(item => item.handle === choice.entry.handle && Math.abs(item.stroke - choice.entry.strokeIn) < .01)) return false;
    studioEntries.push({handle: choice.entry.handle, name: familyName(choice.entry),
      stroke: choice.entry.strokeIn, retractedLength: choice.entry.closedMm / 25.4,
      extendedLength: choice.entry.openMm / 25.4, force: choice.force, adjustable: false,
      variants: [{id: choice.variant.id, title: choice.variant.title || choice.variant.sku, available: true}]});
    return true;
  }).some(Boolean));
  const loads = new Map();
  let selected = null;
  let renderer = null;
  const companions = new Map();
  let loadToken = 0;
  let channel = 0;
  let familyLock = false;
  let strokeLock = false;
  let manual = false;
  let requestedFamily = host.profile.defaultActuatorFamily || null;
  let roll = 0;
  let lastRequirements = '';
  let fitButton = null;
  const state = () => host.state();
  const requirementFor = index => {
    const current = state(), drive = current.result?.channels?.[index];
    if (!drive) return null;
    // Free-drive models report displacement from zero; catalogue pin lengths
    // are absolute and include the entered fully retracted installation bay.
    if (['linear', 'cable', 'finger'].includes(host.profile.type)) {
      const bay = Number(current.parameters.closedSpace) || 0;
      return {...drive, min: bay + drive.min, max: bay + drive.max};
    }
    return drive;
  };
  const requirement = () => {
    const current = requirementFor(channel);
    if (host.profile.type !== 'platform' || !current) return current;
    const drives = state().result?.channels || [];
    if (!drives.length) return current;
    // A platform needs one synchronized model capable of every leg, not a
    // different independently chosen catalogue actuator at each pin circle.
    return {...current,
      min: Math.min(...drives.map(drive => drive.min)),
      max: Math.max(...drives.map(drive => drive.max)),
      peak: Math.max(...drives.map(drive => drive.peak)),
      stroke: Math.max(...drives.map(drive => drive.stroke)),
      sharedLegs: drives.length};
  };
  const isFit = (choice, required) => !!choice && !!required && Number.isFinite(required.peak) &&
    choice.force + .001 >= required.peak &&
    choice.entry.strokeIn + .001 >= required.stroke &&
    choice.entry.closedMm / 25.4 <= required.min + 1e-8 &&
    choice.entry.openMm / 25.4 + 1e-8 >= required.max;
  const geometricFit = (choice, required) => !!choice && !!required &&
    choice.entry.closedMm / 25.4 <= required.min + 1e-8 &&
    choice.entry.openMm / 25.4 + 1e-8 >= required.max;
  const score = (choice, required) =>
    (choice.entry.strokeIn - required.stroke) * 8 +
    Math.max(0, required.min - choice.entry.closedMm / 25.4) * .25 +
    Math.max(0, choice.force - required.peak) / Math.max(required.peak, 1) * 4;
  const constrained = () => choices.filter(choice =>
    (!familyLock || choice.entry.handle === $('#ac-cad-family').value) &&
    (!strokeLock || Math.abs(choice.entry.strokeIn - Number($('#ac-cad-stroke').value)) < .01));
  function candidate(required, preferredFamily = null) {
    const available = constrained().filter(choice =>
      (!preferredFamily || choice.entry.handle === preferredFamily) && isFit(choice, required));
    return available.sort((a, b) =>
      (host.profile.id === 'mobility-linkage'
        ? Number(b.entry.handle === 'micro-utility-actuator') - Number(a.entry.handle === 'micro-utility-actuator')
        : 0) ||
      Number(b.entry.handle === 'super-duty-actuators') - Number(a.entry.handle === 'super-duty-actuators') ||
      score(a, required) - score(b, required) || a.entry.family.localeCompare(b.entry.family))[0] || null;
  }
  function findFixedPinFit(preferredFamily = null) {
    if (!['pivot', 'fourbar', 'doubleleaf'].includes(host.profile.type)) return null;
    const current = state().parameters, result = state().result;
    if (!result || !Number.isFinite(current.baseX) || !Number.isFinite(current.baseY)) return null;
    const fields = host.profile.fields;
    const xField = fields.find(field => field.key === 'baseX');
    const yField = fields.find(field => field.key === 'baseY');
    const span = Math.max(4, Math.min(80, Number(current.span || current.input || 20)));
    const offsets = [0, -.12, .12, -.27, .27, -.45, .45, -.7, .7, -1, 1];
    let best = null;
    for (const x of offsets) for (const y of offsets) {
      if (!x && !y) continue;
      const baseX = current.baseX + x * span, baseY = current.baseY + y * span;
      if (baseX < xField.min || baseX > xField.max || baseY < yField.min || baseY > yField.max) continue;
      try {
        const parameters = {...current, baseX, baseY};
        const solved = window.FGApplicationModel.solve(host.profile.type, parameters);
        const drive = solved.channels[channel];
        if (!Number.isFinite(drive.peak)) continue;
        const choice = candidate(drive, preferredFamily);
        if (!choice) continue;
        const rank = Math.hypot(x, y) * 40 + score(choice, drive);
        if (!best || rank < best.rank) best = {choice, baseX, baseY, rank};
      } catch (_error) { /* Invalid candidate geometry is not a mount. */ }
    }
    return best;
  }
  function loadDrawing(handle, angle) {
    const key = handle + ':' + angle;
    if (!loads.has(key)) {
      const url = new URL(config.catalogueUrl, location.href);
      url.pathname = url.pathname.replace(/fg-lav-catalogue\.js$/, 'fg-lav-' + handle + '-' + angle + '.js');
      url.search = '?v=' + encodeURIComponent(catalogue.buildId || catalogue.builtOn || '1');
      loads.set(key, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url.href;
        script.onload = resolve;
        script.onerror = () => { loads.delete(key); script.remove(); reject(new Error('The selected CAD drawing could not load.')); };
        document.head.append(script);
      }));
    }
    return loads.get(key);
  }
  function setStatus(message, kind = 'info') {
    const output = $('#ac-cad-status');
    output.textContent = message;
    output.dataset.kind = kind;
  }
  function familyOptions() {
    $('#ac-cad-family').innerHTML = families.map(family => `<option value="${esc(family.handle)}">${esc(family.handle === 'electric-column-lift' && isTVTrolley ? 'Electric Column Lift Actuator' : family.name)}</option>`).join('');
  }
  function strokeOptions(preservedStroke = null) {
    const handle = $('#ac-cad-family').value;
    const strokes = [...new Set(entries.filter(entry => entry.handle === handle).map(entry => +entry.strokeIn.toFixed(2)))].sort((a, b) => a - b);
    $('#ac-cad-stroke').innerHTML = strokes.map(stroke => `<option value="${stroke}">${fmt(stroke)} in</option>`).join('');
    if (Number.isFinite(preservedStroke)) {
      const value = +preservedStroke.toFixed(2);
      if (!strokes.some(stroke => Math.abs(stroke - value) < .01))
        $('#ac-cad-stroke').add(new Option(`${fmt(value)} in (unavailable in this family)`, String(value)));
      $('#ac-cad-stroke').value = String(value);
    }
  }
  function modelOptions() {
    const handle = $('#ac-cad-family').value, stroke = Number($('#ac-cad-stroke').value);
    const models = choices.filter(choice => choice.entry.handle === handle && Math.abs(choice.entry.strokeIn - stroke) < .01);
    $('#ac-cad-model').innerHTML = models.map(choice => `<option value="${esc(choice.entry.id)}:${choice.variant.id}">${esc(choice.variant.title || choice.variant.sku || choice.entry.family)}${choice.force ? ' · ' + fmt(choice.force) + ' lbf' : ' · rating unverified'}</option>`).join('');
  }
  function syncControls() {
    if (!selected) return;
    $('#ac-cad-family').value = selected.entry.handle;
    strokeOptions();
    $('#ac-cad-stroke').value = String(+selected.entry.strokeIn.toFixed(2));
    modelOptions();
    $('#ac-cad-model').value = selected.entry.id + ':' + selected.variant.id;
    $('#ac-cad-family-lock').checked = familyLock;
    $('#ac-cad-stroke-lock').checked = strokeLock;
    $('#ac-cad-roll-value').textContent = roll + '°';
  }
  function platformMatchBadge() {
    if (host.profile.type !== 'platform') return;
    const badge = $('.ac-match-status'), label = badge?.querySelector('strong');
    if (!label) return;
    const sharedFit = isFit(selected, requirement());
    badge.classList.toggle('is-found', sharedFit);
    label.textContent = sharedFit ? 'ALL LEGS MATCH' : 'NO SHARED MATCH';
  }
  function specs() {
    const details = $('#ac-cad-selected-specs'), required = requirement();
    if (!details) return;
    if (!selected) { details.textContent = host.profile.type === 'platform' ? 'No single catalogue actuator qualifies for every platform leg at the entered pin circles.' : 'No force-and-mounting match is available at the entered pin locations.'; return; }
    const {entry, variant, force} = selected;
    details.innerHTML = `<article><span>Selected actuator family</span><strong>${esc(familyName(entry))}</strong></article><article><span>Exact force / model</span><strong>${esc(variant.title || variant.sku || 'Catalogue variant')}</strong></article><article><span>Published force rating</span><strong>${force ? fmt(force) + ' lbf' : 'Unverified'}</strong></article><article><span>Catalogue stroke</span><strong>${fmt(entry.strokeIn)} in</strong></article><article><span>Retracted / extended pin length</span><strong>${fmt(entry.closedMm / 25.4)} / ${fmt(entry.openMm / 25.4)} in</strong></article><article><span>Fit at current mounting pins</span><strong>${isFit(selected, required) ? 'Force, stroke and pin range pass' : 'Not qualified'}</strong></article>`;
    $('#ac-cad-product').href = (entry.productUrl || '/products/' + entry.handle) + '?variant=' + encodeURIComponent(variant.id);
  }
  function report() {
    const required = requirement();
    const banner = $('#ac-cad-fitted');
    if (!required || !selected) {
      banner.innerHTML = host.profile.type === 'platform'
        ? '<strong>Catalogue actuator</strong><span>No shared actuator qualifies for all platform legs</span>'
        : '<strong>Catalogue actuator</strong><span>No qualified CAD actuator at these mounting pins</span>';
      $('#ac-cad-banner-link').hidden = true;
      specs();
      if (required) setStatus(host.profile.type === 'platform'
        ? `No single catalogue actuator meets the worst-leg force, stroke and pin-length range across all ${required.sharedLegs} legs. All legs are shown schematically until a shared model qualifies.`
        : 'No available catalogue drawing meets force, stroke and the entire pin-length range. Adjust the mounting geometry or load; the schematic remains visible.', 'warn');
      platformMatchBadge();
      return;
    }
    const {entry, variant, force} = selected;
    banner.innerHTML = `<strong>Fitted actuator</strong><span>${esc(familyName(entry))} · ${fmt(entry.strokeIn)} in stroke · ${esc(variant.title || variant.sku || '')}</span>`;
    const fitted = isFit(selected, required), geometry = geometricFit(selected, required);
    const message = fitted
      ? `${familyName(entry)}: rated ${fmt(force)} lbf vs ${fmt(required.peak)} lbf required; ${fmt(entry.strokeIn)} in stroke vs ${fmt(required.stroke)} in needed${host.profile.type === 'platform' ? ' for the worst of ' + required.sharedLegs + ' legs' : ''}. The CAD is animated at the actual calculated pin distances${host.profile.type === 'platform' ? ' and foreshortened in the 3D projection; the same model is used on every leg' : ''}. Verify brackets, clearances and travel limits before installation.`
      : !geometry
        ? `This ${entry.family} actuator cannot reach the full ${fmt(required.min)}–${fmt(required.max)} in mounting range. The schematic is retained; press Fit actuator or change the mounting pins.`
        : force < required.peak
          ? `The selected model is rated ${fmt(force)} lbf, below the calculated ${fmt(required.peak)} lbf demand. The geometry fits, but it is not a load match.`
          : `The selected model's ${fmt(entry.strokeIn)} in stroke is below the ${fmt(required.stroke)} in travel requirement.`;
    setStatus(message, fitted ? 'ok' : 'warn');
    $('#ac-cad-banner-link').href = (entry.productUrl || '/products/' + entry.handle) + '?variant=' + encodeURIComponent(variant.id);
    $('#ac-cad-banner-link').hidden = false;
    specs();
    platformMatchBadge();
  }
  async function select(choice, mode = 'manual') {
    const token = ++loadToken;
    selected = choice;
    manual = mode === 'manual';
    renderer = null;
    if (host.profile.type === 'platform') companions.clear();
    syncControls();
    report();
    host.setProgress(state().progress);
    if (!choice) return;
    try {
      await loadDrawing(choice.entry.handle, roll);
      if (token !== loadToken) return;
      renderer = engine.create(choice.entry.id, {rollDegrees: roll});
      host.setProgress(state().progress);
    } catch (error) {
      if (token === loadToken) setStatus(error.message + ' The calculated mechanism remains available.', 'warn');
    }
  }
  function refresh() {
    const required = requirement();
    if (!required || !Number.isFinite(required.peak)) {
      selected = null; renderer = null; report(); return;
    }
    const signature = [channel, required.min, required.max, required.peak, required.stroke, familyLock, strokeLock, $('#ac-cad-family').value, $('#ac-cad-stroke').value].join('|');
    if (signature === lastRequirements) return;
    lastRequirements = signature;
    if (manual && selected && selected.entry.handle === $('#ac-cad-family').value &&
        !familyLock && !strokeLock && isFit(selected, required)) { report(); return; }
    const next = candidate(required, requestedFamily);
    if (next && selected?.entry.id === next.entry.id && String(selected.variant.id) === String(next.variant.id)) { report(); return; }
    void select(next, 'auto');
  }
  function refreshCompanions() {
    const channels = state().result?.channels || [];
    const sharedChoice = host.profile.type === 'platform' && isFit(selected, requirement()) ? selected : null;
    for (const index of [...companions.keys()]) {
      if (index >= channels.length) companions.delete(index);
    }
    for (let index = 0; index < channels.length; index++) {
      if (index === channel) continue;
      const required = requirementFor(index);
      const choice = host.profile.type === 'platform' ? sharedChoice : candidate(required);
      const existing = companions.get(index);
      if (!choice) { companions.delete(index); continue; }
      if (existing?.choice.entry.id === choice.entry.id && String(existing.choice.variant.id) === String(choice.variant.id)) continue;
      const item = {choice, renderer: null};
      companions.set(index, item);
      void loadDrawing(choice.entry.handle, roll).then(() => {
        if (companions.get(index) !== item) return;
        item.renderer = engine.create(choice.entry.id, {rollDegrees: roll});
        host.setProgress(state().progress);
      }).catch(() => {
        if (companions.get(index) === item) companions.delete(index);
      });
    }
  }
  function attach() {
    for (const slot of root.querySelectorAll('.ac-cad-actuator-placeholder[data-cad-channel]')) {
      const index = Number(slot.dataset.cadChannel);
      const item = index === channel ? {choice: selected, renderer} : companions.get(index);
      if (!item?.renderer || !geometricFit(item.choice, requirementFor(index)) ||
          (host.profile.type === 'platform' && !isFit(item.choice, requirement()))) continue;
      const base = [Number(slot.dataset.baseX) * 25.4, -Number(slot.dataset.baseY) * 25.4];
      const pin = [Number(slot.dataset.pinX) * 25.4, -Number(slot.dataset.pinY) * 25.4];
      try {
      const holder = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      holder.setAttribute('transform', 'scale(0.0393700787402,-0.0393700787402)');
      if (host.profile.type === 'platform') {
        const actual = state().result.samples[Math.round(state().progress * 240)]?.channels?.[index]?.length * 25.4;
        const projected = Math.hypot(pin[0] - base[0], pin[1] - base[1]);
        if (!Number.isFinite(actual) || actual <= 0 || projected <= 0) continue;
        item.renderer.placeBetween([0, 0], [actual, 0]);
        const projection = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        projection.setAttribute('transform', `translate(${base[0]},${base[1]}) rotate(${Math.atan2(pin[1] - base[1], pin[0] - base[0]) * 180 / Math.PI}) scale(${projected / actual},1)`);
        projection.append(item.renderer.element);
        holder.append(projection);
      } else {
        holder.append(item.renderer.element);
        if (item.choice.entry.motionType === 'column') {
          item.renderer.setLength(Math.hypot(pin[0] - base[0], pin[1] - base[1]));
          item.renderer.element.setAttribute('transform', `translate(${base[0]},${base[1]}) rotate(${Math.atan2(pin[1] - base[1], pin[0] - base[0]) * 180 / Math.PI})`);
        } else item.renderer.placeBetween(base, pin);
      }
      slot.replaceChildren(holder);
      } catch (error) {
        slot.replaceChildren();
        setStatus('A catalogue CAD drawing cannot be placed at this position. Review that drive’s mounting dimensions or select another model.', 'warn');
      }
    }
  }
  function clearLabelCollisions() {
    const svg = $('#ac-scene svg');
    if (!svg) return;
    const labels = [...svg.querySelectorAll('.ac-annotations text')];
    const svgBox = svg.getBoundingClientRect();
    if (!svgBox.width) return;
    const pixelsPerUnit = svgBox.width / svg.viewBox.baseVal.width;
    const placed = [];
    const overlaps = (a, b) => Math.min(a.right, b.right) - Math.max(a.left, b.left) > 2 &&
      Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 2;
    for (const label of labels) {
      const badge = label.parentElement?.classList.contains('ac-dimension') ? label.parentElement.querySelector('rect') : null;
      const step = Math.max(22, Math.ceil(label.getBoundingClientRect().height / pixelsPerUnit) + 7);
      const offsets = [[0, 0]];
      for (let ring = 1; ring <= 5; ring++) {
        for (const [x, y] of [[0, -ring], [0, ring], [-ring, 0], [ring, 0], [-ring, -ring], [ring, -ring], [-ring, ring], [ring, ring]]) {
          offsets.push([x * step, y * step]);
        }
      }
      for (const [x, y] of offsets) {
        const transform = x || y ? `translate(${x} ${y})` : '';
        label.setAttribute('transform', transform);
        if (badge) badge.setAttribute('transform', transform);
        const box = label.getBoundingClientRect();
        if (box.left >= svgBox.left + 3 && box.right <= svgBox.right - 3 &&
            box.top >= svgBox.top + 3 && box.bottom <= svgBox.bottom - 3 &&
            !placed.some(previous => overlaps(box, previous))) break;
      }
      placed.push(label.getBoundingClientRect());
    }
  }
  function install() {
    const footer = $('.ac-stage-footer');
    if (!footer || $('#ac-cad-chooser')) return;
    const banner = document.createElement('div');
    banner.id = 'ac-cad-banner';
    banner.innerHTML = '<div id="ac-cad-fitted"><strong>Catalogue actuator</strong><span>Finding a match…</span></div><a id="ac-cad-banner-link" target="_blank" rel="noopener noreferrer" hidden>View exact model ↗</a>';
    $('.ac-stage').prepend(banner);
    const chooser = document.createElement('div');
    chooser.id = 'ac-cad-chooser';
    chooser.innerHTML = '<label>Actuator family<select id="ac-cad-family"></select></label><label class="ac-cad-lock"><input id="ac-cad-family-lock" type="checkbox"> Only use this model</label><label>Stroke<select id="ac-cad-stroke"></select></label><label class="ac-cad-lock"><input id="ac-cad-stroke-lock" type="checkbox"> Only use this stroke</label><label>Force / model<select id="ac-cad-model"></select></label><div class="ac-cad-rotate"><span>Rotate actuator SVG</span><button id="ac-cad-roll-minus" type="button">−90°</button><output id="ac-cad-roll-value">0°</output><button id="ac-cad-roll-plus" type="button">+90°</button></div><p id="ac-cad-status" role="status"></p>';
    footer.prepend(chooser);
    fitButton = document.createElement('button');
    fitButton.id = 'ac-cad-fit';
    fitButton.className = 'ac-primary';
    fitButton.type = 'button';
    fitButton.textContent = 'Fit actuator';
    const actions = document.createElement('div');
    actions.className = 'ac-cad-actions';
    actions.append(fitButton);
    const sceneActions = $('#ac-scene-actions');
    if (sceneActions) sceneActions.prepend(actions);
    else footer.prepend(actions);
    const specs = document.createElement('section');
    specs.innerHTML = '<h3>Selected CAD actuator</h3><div id="ac-cad-selected-specs" class="ac-technical-specs"></div><a id="ac-cad-product" target="_blank" rel="noopener noreferrer">View exact catalogue model ↗</a>';
    $('#ac-panel-spec').append(specs);
    familyOptions();
    $('#ac-cad-family').addEventListener('change', () => {
      const handle = $('#ac-cad-family').value;
      requestedFamily = handle;
      const lockedStroke = strokeLock ? Number($('#ac-cad-stroke').value) : null;
      strokeOptions(lockedStroke);
      if (!strokeLock) $('#ac-cad-stroke').selectedIndex = 0;
      modelOptions();
      const match = candidate(requirement(), handle);
      if (match) { void select(match, 'manual'); return; }
      const mount = findFixedPinFit(handle);
      if (mount) {
        host.setParameters({baseX: mount.baseX, baseY: mount.baseY});
        void select(mount.choice, 'manual');
        setStatus(`Automatically fitted ${mount.choice.entry.family} by moving the fixed pin to (${fmt(mount.baseX)}, ${fmt(mount.baseY)}) in. Verify a rigid mount and bracket clearance.`, 'ok');
        return;
      }
      void select(null, 'auto');
      setStatus(host.profile.type === 'platform'
        ? `No ${handle.replace(/-/g, ' ')} model qualifies across all ${requirement()?.sharedLegs || 3} platform legs${strokeLock ? ' with the locked ' + fmt(lockedStroke) + ' in stroke' : ''}. All legs remain schematic; choose a suitable family or adjust the geometry and load.`
        : strokeLock
          ? `No ${handle.replace(/-/g, ' ')} actuator fits these pins with the locked ${fmt(lockedStroke)} in stroke. The stroke lock was retained.`
          : `No force-and-pin fit is available in ${handle.replace(/-/g, ' ')} at these mounting positions. Adjust the mount or choose another family.`, 'warn');
    });
    $('#ac-cad-stroke').addEventListener('change', () => {
      manual = true; modelOptions();
      const choice = choices.find(item => item.entry.id + ':' + item.variant.id === $('#ac-cad-model').value);
      void select(choice);
    });
    $('#ac-cad-model').addEventListener('change', () => {
      const choice = choices.find(item => item.entry.id + ':' + item.variant.id === $('#ac-cad-model').value);
      if (choice) requestedFamily = choice.entry.handle;
      void select(choice);
    });
    $('#ac-cad-family-lock').addEventListener('change', event => { familyLock = event.target.checked; manual = false; lastRequirements = ''; refresh(); });
    $('#ac-cad-stroke-lock').addEventListener('change', event => { strokeLock = event.target.checked; manual = false; lastRequirements = ''; refresh(); });
    $('#ac-cad-fit').addEventListener('click', () => {
      manual = false; lastRequirements = '';
      const match = candidate(requirement(), requestedFamily);
      if (match) void select(match, 'auto');
      else {
        const mount = findFixedPinFit(requestedFamily);
        if (mount) {
          const old = state().parameters;
          host.setParameters({baseX: mount.baseX, baseY: mount.baseY});
          void select(mount.choice, 'auto');
          setStatus(`Fit actuator moved the fixed pin from (${fmt(old.baseX)}, ${fmt(old.baseY)}) to (${fmt(mount.baseX)}, ${fmt(mount.baseY)}) in. Verify that this position has a real rigid mounting structure and adequate bracket clearance.`, 'ok');
        } else setStatus(host.profile.type === 'platform'
          ? `No single ${requestedFamily ? requestedFamily.replace(/-/g, ' ') : 'catalogue'} model fits the force, stroke and pin-length range of all ${requirement()?.sharedLegs || 3} platform legs${strokeLock ? ' with the locked stroke' : ''}. Adjust the platform geometry or load, or choose another family.`
          : 'No force-and-mounting fit exists within the selected locks and permitted pin ranges. Unlock a model or stroke, or change the mounting geometry.', 'warn');
      }
    });
    for (const [id, delta] of [['ac-cad-roll-minus', -90], ['ac-cad-roll-plus', 90]]) {
      $('#' + id).addEventListener('click', () => { roll = (roll + delta + 360) % 360; $('#ac-cad-roll-value').textContent = roll + '°'; if (selected) void select(selected, manual ? 'manual' : 'auto'); });
    }
    refresh();
  }
  window.FGApplicationCAD = {canRender(index) {
    const item = index === channel ? {choice: selected, renderer} : companions.get(index);
    return !!item?.renderer && geometricFit(item.choice, requirementFor(index)) &&
      (host.profile.type !== 'platform' || isFit(item.choice, requirement()));
  }, get selection() { return selected; }};
  document.addEventListener('fgapplication:draw', event => {
    if (event.detail?.root !== root || !$('#ac-cad-chooser')) return;
    const current = window.FGApplicationState?.channel || 0;
    if (current !== channel) { channel = current; manual = false; lastRequirements = ''; }
    refresh();
    refreshCompanions();
    attach();
    platformMatchBadge();
    clearLabelCollisions();
  });
  install();
  if (studioColumnsAdded) host.setParameters({});
})();
